import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Shuffle,
  RotateCcw,
  ArrowLeftRight,
} from "lucide-react";
import { Flashcard, FlashcardData } from "./Flashcard";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
    scale: 0.95,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
    scale: 0.95,
  }),
};

interface FlashcardSetProps {
  contents: { [key: string]: any };
  className?: string;
}

type SideMode = "term" | "definition";

export function FlashcardSet({ contents, className }: FlashcardSetProps) {
  const cards = contents.flashcards as FlashcardData[];

  const [order, setOrder] = useState<number[]>(() => cards.map((_, i) => i));
  const [index, setIndex] = useState(0);
  const [side, setSide] = useState<SideMode>("term");
  const [direction, setDirection] = useState<1 | -1>(1);

  const total = cards.length;
  const rawCard = cards[order[index]];

  /* ---------- derived card (swap here) ---------- */

  const currentCard = useMemo<FlashcardData>(() => {
    if (!rawCard) return rawCard;

    if (side === "term") return rawCard;

    return {
      ...rawCard,
      term: rawCard.definition,
      definition: rawCard.term,
    };
  }, [rawCard, side]);

  /* ---------- helpers ---------- */

  const goPrev = useCallback(() => {
    setDirection(-1);
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setDirection(1);
    setIndex((i) => Math.min(total - 1, i + 1));
  }, [total]);

  const restart = () => setIndex(0);

  const shuffle = () => {
    const shuffled = [...order];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setOrder(shuffled);
    setIndex(0);
  };

  /* ---------- keyboard ---------- */

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goNext, goPrev]);

  /* ---------- progress ---------- */

  const progressPct = useMemo(() => {
    if (total === 0) return 0;
    return ((index + 1) / total) * 100;
  }, [index, total]);

  if (!currentCard) return null;

  /* ---------- shared button styles ---------- */

  const navButton =
    "flex items-center gap-1 px-3 py-2 rounded-lg text-theme-0 transition " +
    "hover:bg-theme-1 active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none";

  const textAction =
    "inline-flex items-center gap-1 text-xs transition " +
    "text-theme-1 hover:scale-110 active:scale-90";

  return (
    <div
      className={cn(
        "w-full max-w-xl mx-auto flex flex-col items-center",
        className
      )}
    >
      {/* Instruction */}
      <div className="mb-3 text-center text-xs text-theme-1">
        <span className="pr-8">
          Click or <kbd className="px-1 py-0.5 rounded bg-theme-1">Space</kbd>{" "}
          to flip
        </span>
        <kbd className="px-1 py-0.5 rounded bg-theme-1">←</kbd>
        <kbd className="px-1 py-0.5 rounded bg-theme-1">→</kbd> to navigate
      </div>

      {/* Card + ambient blur */}
      <div className="relative w-full flex justify-center">
        {/* Left blur */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute left-[-50px] top-1/2 -translate-y-1/2",
            "h-64 w-64 rounded-full blur-2xl opacity-20"
          )}
          style={{
            background: "radial-gradient(circle, rgba(99,102,241,1))",
          }}
        />

        {/* Right blur */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute right-[-50px] top-1/2 -translate-y-1/2",
            "h-64 w-64 rounded-full blur-2xl opacity-20"
          )}
          style={{
            background: "radial-gradient(circle, rgba(16,185,129,1))",
          }}
        />

        {/* Card */}
        <div className="relative w-full flex justify-center min-h-[360px] z-10 mb-5">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={order[index]} // 🔑 critical
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.15,
                ease: "easeOut",
              }}
              className="absolute"
            >
              <Flashcard card={currentCard} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-4 flex items-center justify-between w-full">
        <button onClick={goPrev} disabled={index === 0} className={navButton}>
          <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Prev
        </button>

        <div className="text-xs text-theme-1">
          {index + 1} / {total}
        </div>

        <button
          onClick={goNext}
          disabled={index === total - 1}
          className={navButton}
        >
          Next
          <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1 w-full rounded-full bg-theme-2 overflow-hidden">
        <div
          className="h-full bg-brand-gradient transition-all rounded-full duration-300"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Actions */}
      <div className="mt-3 flex items-center justify-between w-full">
        {/* Left: mode toggle */}
        <button
          onClick={() => setSide((s) => (s === "term" ? "definition" : "term"))}
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs",
            "bg-theme-1 text-theme-1 transition",
            "hover:bg-theme-2 hover:text-theme-0 active:scale-[0.96]"
          )}
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
          Showing: <strong>{side}</strong>
        </button>

        {/* Center: restart / shuffle */}
        <div className="flex gap-4">
          <button onClick={restart} className={textAction}>
            <RotateCcw className="h-3.5 w-3.5" />
            Restart
          </button>

          <button onClick={shuffle} className={textAction}>
            <Shuffle className="h-3.5 w-3.5" />
            Shuffle
          </button>
        </div>
      </div>
    </div>
  );
}
